import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import {
  RefreshCw,
  Loader2,
  ExternalLink,
  CheckCircle2,
  Clock,
  AlertOctagon,
  TrendingUp,
  Eye,
} from "lucide-react";

function StatCard({ label, value, sublabel, tone = "default", testid }) {
  const toneClass = {
    default: "bg-white",
    danger: "bg-red-50 border-red-300",
    success: "bg-emerald-50 border-emerald-300",
    info: "bg-blue-50 border-blue-300",
  }[tone];
  return (
    <div
      className={`border-2 border-zinc-950 p-4 ${toneClass}`}
      data-testid={testid}
    >
      <div className="label-xs">{label}</div>
      <div className="font-display text-4xl font-black tabular-nums leading-none mt-2">
        {value}
      </div>
      {sublabel && (
        <div className="mt-1.5 text-[11px] text-zinc-600 font-mono leading-tight">
          {sublabel}
        </div>
      )}
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function InboundScans() {
  const [data, setData] = useState({ runs: [], stats: {} });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/teaser-runs", { params: { limit: 200 } });
      setData(r.data);
    } catch {
      // silent — page shows empty state if endpoint errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { runs = [], stats = {} } = data;

  return (
    <div data-testid="inbound-scans-page">
      <PageHeader
        eyebrow="Module 07 · Inbound Scans"
        title="Who's running the free scan."
        description="Every prospect who self-identified by running your hero scan widget — with conversion attribution joined against the schedule-page submissions. The pending ones are your hot retargeting list."
        actions={
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            className="rounded-sm h-10 border-zinc-400 hover:bg-zinc-50"
            data-testid="inbound-refresh-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        }
        testid="inbound-header"
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inbound-stats">
          <StatCard
            testid="stat-runs-7d"
            label="Scans · last 7d"
            value={stats.runs_7d ?? 0}
            sublabel={`${stats.runs_30d ?? 0} in last 30d · ${stats.total ?? 0} all-time`}
          />
          <StatCard
            testid="stat-qualified-7d"
            label="Qualified · ≥10 issues · 7d"
            value={stats.qualified_7d ?? 0}
            sublabel="Prospects with material WCAG exposure"
            tone="danger"
          />
          <StatCard
            testid="stat-converted-7d"
            label="Converted · 7d"
            value={stats.converted_7d ?? 0}
            sublabel={`${stats.converted_all_time ?? 0} all-time bookings`}
            tone="success"
          />
          <StatCard
            testid="stat-conversion-rate"
            label="Conversion rate · 7d"
            value={`${stats.conversion_rate_7d_pct ?? 0}%`}
            sublabel="scans → schedule-page submissions"
            tone="info"
          />
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-zinc-950" data-testid="inbound-table-wrapper">
          <div className="bg-zinc-100 px-4 py-2.5 flex items-center justify-between border-b border-zinc-200">
            <div className="label-xs">Recent scans · {runs.length}</div>
            <div className="text-[10px] font-mono text-zinc-500">
              Conversion = same hostname appears in /schedule submissions
            </div>
          </div>

          {loading && runs.length === 0 && (
            <div className="py-16 text-center">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-zinc-400" />
            </div>
          )}

          {!loading && runs.length === 0 && (
            <div className="py-16 text-center text-sm text-zinc-500" data-testid="inbound-empty">
              No inbound scans yet. Once visitors hit the hero scan widget at
              <span className="font-mono mx-1 text-zinc-700">/</span>
              they&apos;ll appear here in real time.
            </div>
          )}

          {runs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Time</th>
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider">URL</th>
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-20">Issues</th>
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-20">High</th>
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-40">Status</th>
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Top Categories</th>
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-32">Geo</th>
                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-28">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => {
                    const qualified = (r.total_issues || 0) >= 10;
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-zinc-100 hover:bg-zinc-50 align-top"
                        data-testid={`inbound-row-${r.id}`}
                      >
                        <td className="px-3 py-2 font-mono text-[11px] text-zinc-700 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-zinc-400" />
                            {relativeTime(r.created_at)}
                          </div>
                          {r.served_from_cache && (
                            <div className="text-[9px] text-zinc-400 mt-0.5">(cached · repeat)</div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                          >
                            {r.host}
                            <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
                          </a>
                        </td>
                        <td className={`px-3 py-2 font-mono font-bold tabular-nums ${qualified ? "text-red-700" : ""}`}>
                          {r.total_issues}
                        </td>
                        <td className="px-3 py-2 font-mono text-red-700 font-bold tabular-nums">
                          {r.high_severity_count}
                        </td>
                        <td className="px-3 py-2">
                          {r.converted ? (
                            <div
                              className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 px-2 py-1"
                              data-testid={`inbound-status-converted-${r.id}`}
                              title={`Booked: ${r.converted_name || "—"} · ${r.converted_email || "—"}`}
                            >
                              <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                                Converted
                              </span>
                            </div>
                          ) : qualified ? (
                            <div
                              className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-300 px-2 py-1"
                              data-testid={`inbound-status-hot-${r.id}`}
                            >
                              <TrendingUp className="w-3 h-3 text-amber-700" />
                              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
                                Hot Lead
                              </span>
                            </div>
                          ) : (
                            <div
                              className="inline-flex items-center gap-1.5 bg-zinc-100 border border-zinc-300 px-2 py-1"
                              data-testid={`inbound-status-pending-${r.id}`}
                            >
                              <Eye className="w-3 h-3 text-zinc-600" />
                              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">
                                Scanned
                              </span>
                            </div>
                          )}
                          {r.converted && r.converted_email && (
                            <div className="text-[10px] font-mono text-zinc-500 mt-1 truncate" title={r.converted_email}>
                              {r.converted_email}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.top_categories?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {r.top_categories.map((c) => (
                                <span
                                  key={c.name}
                                  className="inline-flex items-center gap-1 bg-zinc-100 border border-zinc-300 px-1.5 py-0.5 font-mono text-[10px]"
                                >
                                  {c.name}
                                  <span className="text-red-700 font-bold">×{c.count}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[10px] text-zinc-400 italic">clean</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px] text-zinc-600 leading-tight">
                          {r.geo_city || r.geo_country ? (
                            <>
                              {r.geo_city && <div className="truncate" title={r.geo_city}>{r.geo_city}</div>}
                              {r.geo_country && (
                                <div className="text-zinc-400">{r.geo_country_code || r.geo_country}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-zinc-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px] text-zinc-500 truncate" title={r.ip || ""}>
                          {r.ip || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-zinc-50 border border-zinc-200 p-4 flex items-start gap-3">
          <AlertOctagon className="w-4 h-4 mt-0.5 text-zinc-500 shrink-0" />
          <p className="text-xs text-zinc-700 leading-relaxed">
            <span className="font-bold">Retargeting tip:</span> Filter the rows by <span className="font-mono">Hot Lead</span> status to surface prospects with material exposure who scanned but never booked. These are your highest-intent outbound list — they self-identified and walked away. Pitch them via Site Auditor &rarr; Send Pitch.
          </p>
        </div>
      </div>
    </div>
  );
}
